import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {StyledMrcDialog} from './styled';
import {Button, CloseIcon, Divider, IconButton, Layout, Text} from '@phonectas/unifon-ui-kit';
import MrcContent from './components/MrcContent';
import LinearLoader from 'common/components/customLoader/components/LinearLoader';
import useMrcStore from 'common/stores/mrcStore';
import {shallow} from 'zustand/shallow';
import {useGetTemplateName} from 'common/hooks/useGetMrcTemplate';
import {MrcEntryCreateRequest} from 'common/types/store/mrcTypes';

interface AddNewMrcDialogProps {
    show: boolean;
    onClose: () => void;
    onSend: (templateId: number | string, formData: Partial<MrcEntryCreateRequest>) => void | Promise<void>;
    templateId: string | number;
    width?: number;
    noOverflow?: boolean;
    noPadding?: boolean;
}

const AddNewMrcDialog: React.FC<AddNewMrcDialogProps> = ({
	show,
	onClose,
	onSend,
	templateId,
	width,
}) => {
	const [t] = useTranslation();
	const {
		orgIsLoading,
		isLoading,
	} = useMrcStore((state) => ({
		orgIsLoading: state.orgIsLoading,
		isLoading: state.isLoading,
	}), shallow);
	const [canSendMrc, setCanSendMrc] = useState(false);
	const [formData, setFormData] = useState<Partial<MrcEntryCreateRequest>>({});
	const getTemplate = useGetTemplateName(templateId);

	const handleSend = useCallback(() => {
		onSend(templateId, formData);
	}, [onSend, templateId, formData]);

	const getRequiredFields = useCallback(() => {
		if (getTemplate && 'required_fields' in getTemplate) {
			return getTemplate.required_fields;
		}
		return [];
	}, [getTemplate]);

	return (
		<StyledMrcDialog
			show={show}
			onClose={onClose}
			$width={width ?? 600}
			$noPadding={true}
		>
			<Layout fullWidth>
				<Layout
					fullWidth
					justifyContent="space-between"
					style={{padding: '16px 16px 0 16px'}}
				>
					<Text variant="h1">{`${getTemplate?.description}`}</Text>
					<IconButton onClick={onClose} aria-label={t('Close dialog')}>
						<CloseIcon />
					</IconButton>
				</Layout>
				<Divider />
				<Layout fullWidth style={{padding: '0px 16px'}}>
					<MrcContent
						canSendMrc={setCanSendMrc}
						requiredFields={getRequiredFields()}
						onFormDataChange={setFormData}
					/>
				</Layout>
				<Layout fullWidth style={{height: 4}}>
					<LinearLoader isLoading={isLoading || orgIsLoading} />
				</Layout>
				<Divider />
				<Layout
					fullWidth
					justifyContent="end"
					style={{padding: '0px 16px 16px 16px'}}
				>
					<Button
						variant="primary"
						color="success"
						onClick={handleSend}
						disabled={!canSendMrc}
					>
						{t('Send')}
					</Button>
					<Button variant="secondary" color="secondary" onClick={onClose}>
						{t('Close')}
					</Button>
				</Layout>
			</Layout>
		</StyledMrcDialog>
	);
}

export default AddNewMrcDialog;